import { CredentialRequestOptionsJSON, PublicKeyCredentialWithAssertionJSON, get } from '@github/webauthn-json';
import { AxiosError, AxiosResponse } from 'axios';
import { runInAction } from 'mobx';
import { UiResponse } from '../CommonInterfaces';
import { API } from '../util/ApiUtil';
import { filterForwardParamsFromCurrentParams } from '../util/ForwardParamsUtil';
import BaseLoginStore from './BaseLoginStore';
import RootStore from './RootStore';

type PublicKeyCredentialRequestOptionsJSON = CredentialRequestOptionsJSON['publicKey'];

interface AssertionRequest {
  publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptionsJSON;
  username: string;
}

export default class BiometryLoginStore extends BaseLoginStore {
  constructor(rootStore: RootStore) {
    super(rootStore);
  }

  public login() {
    this.handleLogin();
  }

  private throwBiometryCancelError = (e: AxiosError) => {
    e.response = { ...e.response, data: { notice: { type: 'info', message: 'biometry.authentication.error' } } } as AxiosResponse;
    throw e;
  };

  protected handleLogin = async () => {
    const authType = 'biometry';
    runInAction(() => {
      this.submitting = true;
    });

    try {
      const assertionRequest = await this.initiateAuthentication();
      const credential = await get({ publicKey: assertionRequest.publicKeyCredentialRequestOptions }).catch((e) => this.throwBiometryCancelError(e));
      const response = await this.submitCredentialForAuthentication(credential);
      this.handleSuccess(response, authType);
    } catch (e: Error | string | any) {
      this.handleError(e, authType);
    }
  };

  private async initiateAuthentication(): Promise<AssertionRequest> {
    const response = await API.post('/biometry/authentication/initiate');
    return response.data;
  }

  private async submitCredentialForAuthentication(credential: PublicKeyCredentialWithAssertionJSON): Promise<AxiosResponse<UiResponse>> {
    const params = filterForwardParamsFromCurrentParams({
      ...this.routeStore.decodedParams,
      rememberMe: this.rememberMeStore.rememberMe
    });
    return API.post(
      `/biometry/authentication/complete${params}`,
      {
        id: credential.id,
        type: credential.type,
        response: { ...credential.response },
        clientExtensionResults: { ...credential.clientExtensionResults }
      },
      { withCredentials: true }
    );
  }
}
